import React, { Component } from 'react';
import PropTypes from 'prop-types';
import windowResizeFactory from 'components/window-resize';

/**
 * A Higher Order Component that calculates the parents width and height
 * and decorates the wrapped component with those dimensions via props.
 * The wrapped component can then use those dimensions to size itself.
 * While normally this can simply be achieved with CSS, the use case for this HOC
 * is for calculating the optimum size of SVG and Canvas elements.
 *
 * @param {React.Component} WrappedComponent - the component to enhance
 * @returns {React.Component}
 **/
const autoSizeFromParentBoundsFactory = WrappedComponent => {
  const WrappedComponentWithWindowResize = windowResizeFactory(
    WrappedComponent
  );

  class AutoSizeFromBounds extends Component {
    static propTypes = {
      hideOnResize: PropTypes.bool,
      onWindowResizeStart: PropTypes.func,
      onWindowResizeEnd: PropTypes.func,
    };

    static defaultProps = {
      hideOnResize: false,
      onWindowResizeStart: () => {},
      onWindowResizeEnd: () => {},
    };

    constructor(props) {
      super(props);

      this._getWidthAndHeightFromParent = this._getWidthAndHeightFromParent.bind(
        this
      );
      this._updateDimensions = this._updateDimensions.bind(this);
      this._renderWrappedComponent = this._renderWrappedComponent.bind(this);

      this._onWindowResizeStart = this._onWindowResizeStart.bind(this);
      this._onWindowResizeEnd = this._onWindowResizeEnd.bind(this);
    }

    state = {
      width: 0,
      height: 0,
    };

    componentDidMount() {
      this._updateDimensions();
    }

    componentDidUpdate() {
      /**
       * Update size of the component when parent size changes.
       * react-grid-layout recalculates size of an item after mounting,
       * therefore we need to update the state.
       */
      const { width, height } = this._getWidthAndHeightFromParent();
      if (this.state.width !== width || this.state.height !== height) {
        this._updateDimensions();
      }
    }

    _getWidthAndHeightFromParent() {
      const parentNode = this.refs.autoSizeWrapper.parentNode;
      return { width: parentNode.offsetWidth, height: parentNode.offsetHeight };
    }

    _updateDimensions() {
      const { width, height } = this._getWidthAndHeightFromParent();
      this.setState({ width, height });
    }

    /**
     * Event handler for `window.resize`. Fired when the browser is resizing, debounced against
     * resizeDebounceMs number provided via props.
     **/
    _onWindowResizeStart() {
      if (this.props.onWindowResizeStart) {
        this.props.onWindowResizeStart();
      }
    }

    /**
     * Event handler for `window.resize-end`. Fired when the browser completes resizing.
     **/
    _onWindowResizeEnd() {
      this._updateDimensions();

      if (this.props.onWindowResizeEnd) {
        this.props.onWindowResizeEnd();
      }
    }

    _renderWrappedComponent() {
      const { width, height } = this.state;

      return (
        <WrappedComponentWithWindowResize
          {...this.props}
          width={width}
          height={height}
          onWindowResizeStart={this._onWindowResizeStart}
          onWindowResizeEnd={this._onWindowResizeEnd}
        />
      );
    }

    render() {
      const { width, height } = this.state;

      return (
        <div ref="autoSizeWrapper">
          {width > 0 && height > 0 ? this._renderWrappedComponent() : null}
        </div>
      );
    }
  }

  return AutoSizeFromBounds;
};

export default autoSizeFromParentBoundsFactory;
export { autoSizeFromParentBoundsFactory };
